import { useCallback } from 'react';
import useSWR, { type Fetcher } from 'swr';

import { carsList } from '~/data/generated/endpoints/cars/cars';
import type { Car } from '~/data/generated/model';
import { CarsListResponse } from '~/data/generated/zod/cars/cars.zod';
import { parseApiResponse } from '~/data/parseApiResponse';

// Shares `CarSerializer` with `useCar`, so it inherits the same base — whose
// known nullability gaps and degenerate `property.portfolio` were fixed at
// source by AMP-619. Still parsed report-only under this `kind`; see the note in
// `~/data/hooks/useCar`.
const carsFetcher: Fetcher<Car[], readonly ['property', propertyId: string, 'cars']> = async ([
  _,
  propertyId,
]) => {
  const { data } = await carsList({ property_id: propertyId });
  return parseApiResponse(CarsListResponse, data, 'PropertyCars');
};

export const usePropertyCars = (propertyId: string | null) => {
  const {
    data: cars,
    isLoading: loading,
    mutate,
  } = useSWR(propertyId ? (['property', propertyId, 'cars'] as const) : null, carsFetcher);
  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return { cars, loading, refresh };
};
